// Settings.jsx — страница «Настройки аккаунта» админ-панели.
// Левая колонка — карточка с фото профиля и кнопкой замены.
// Правая — форма личных данных (имя/фамилия/локация/статус).
// Снизу — отдельная «опасная зона» с кнопкой выхода из аккаунта.

import React, { useState, useEffect, useRef } from 'react';
import cl from './Settings.module.css';
import { useLoader } from '../../hooks/useLoader';
import UserService from '../../API/UserService';
import { FOLDER_PROFILE } from '../../API/API';
import { useNavigate } from 'react-router-dom';
import { removeAuthToken } from '../../utils/authUtils';
import ava from './img/1.png';
import {
    IconSettings, IconUser, IconCheck, IconAlert, IconLogout, IconRefresh,
    IconUpload, IconClose,
} from '../../components/Icons';


const SettingsAccount = () => {
    const topRef = useRef(null);
    const fileInputRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (topRef.current) {
            topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, []);

    const [firstName, setFirstName] = useState('');
    const [city, setCity] = useState('');
    const [country, setCountry] = useState('');
    const [status, setStatus] = useState('');
    const [basicCourse, setbasicCourse] = useState('');
    const [lang, setlang] = useState('');
    const [lastName, setlastName] = useState('');
    const [photo, setPhoto] = useState('');
    const [imgFile, setImgFile] = useState(null);
    const [imgPreview, setImgPreview] = useState(null);
    const [errors, setErrors] = useState({});
    const [changes, setChanges] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [showExitConfirm, setShowExitConfirm] = useState(false);

    const [fetchData, isLoading, loadingError] = useLoader(async () => {
        const dataLec = await UserService.getMyProfile();
        setFirstName(dataLec.firstName || '');
        setCity(dataLec.city || '');
        setCountry(dataLec.country || '');
        setStatus(dataLec.status || '');
        setbasicCourse(dataLec.basicCourse || '');
        setlang(dataLec.lang || '');
        setlastName(dataLec.lastName || '');
        setPhoto(dataLec.photo || '');
    });

    useEffect(() => {
        fetchData();
    }, [changes]);

    // Локальное превью только что выбранного файла
    useEffect(() => {
        if (!imgFile) {
            setImgPreview(null);
            return;
        }
        const url = URL.createObjectURL(imgFile);
        setImgPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [imgFile]);

    const handlePickFile = (e) => {
        const file = e.target.files?.[0];
        if (file) setImgFile(file);
    };

    const saveProfile = async () => {
        setIsSaving(true);
        try {
            await UserService.putMyProfile(get_res, {
                photo: imgFile,
                basicCourse,
                city,
                country,
                firstName,
                lang,
                lastName,
                status,
            });
        } catch (error) {
            setErrors({ ...errors, errorServer: 'Ошибка изменения профиля' });
        } finally {
            setIsSaving(false);
        }
    };

    const get_res = (res) => {
        if (res === 'Изменено') {
            if (imgFile != null) {
                navigate(0);
            }
            setChanges({ ...changes + '1' });
            setErrors({ ...errors, message: 'Изменено' });
            setTimeout(() => setErrors({}), 2500);
        } else if (res === 'Имя файла уже сущетсвует') {
            setErrors({ ...errors, errorServer: 'Измени название изображения' });
            setTimeout(() => setErrors({}), 2500);
        }
    };

    const exitUser = () => {
        removeAuthToken();
        navigate('/');
        navigate(0);
    };

    const displayedPhoto = imgPreview
        ? imgPreview
        : photo
            ? FOLDER_PROFILE + photo
            : ava;

    return (
        <div ref={topRef} className={cl.page_wrapper}>
            <div className={cl.wrap_content_1}>

                {/* Шапка-карточка */}
                <div className={cl.pageHeaderV2}>
                    <div className={cl.pageHeaderLeft}>
                        <div className={cl.pageHeaderIcon}>
                            <IconSettings width={22} height={22}/>
                        </div>
                        <div className={cl.pageHeaderText}>
                            <div className={cl.pageHeaderTopRow}>
                                <h1 className={cl.pageHeaderTitle}>Настройки аккаунта</h1>
                            </div>
                            <div className={cl.breadcrumbs}>
                                АдминПанель · Профиль · Личные данные
                            </div>
                        </div>
                    </div>
                </div>

                {/* Toast: успех или ошибка */}
                {(errors.message || errors.errorServer) && (
                    <div className={`${cl.toast} ${errors.message ? cl.toastSuccess : cl.toastError}`}>
                        <span className={cl.toastIcon}>
                            {errors.message
                                ? <IconCheck width={16} height={16}/>
                                : <IconAlert width={16} height={16}/>}
                        </span>
                        <span className={cl.toastMsg}>
                            {errors.message || errors.errorServer}
                        </span>
                        <button
                            className={cl.toastClose}
                            onClick={() => setErrors({})}
                            aria-label="Закрыть"
                        >
                            <IconClose width={14} height={14}/>
                        </button>
                    </div>
                )}

                {/* Загрузка данных */}
                {isLoading && (
                    <div className={cl.loadingHint}>
                        <IconRefresh width={14} height={14}/>
                        <span>Загрузка данных профиля…</span>
                    </div>
                )}
                {loadingError && (
                    <div className={`${cl.toast} ${cl.toastError}`}>
                        <span className={cl.toastIcon}>
                            <IconAlert width={16} height={16}/>
                        </span>
                        <span className={cl.toastMsg}>Не удалось загрузить профиль</span>
                    </div>
                )}

                <div className={cl.grid}>
                    {/* ===== Левая колонка: фото ===== */}
                    <aside className={cl.photoCard}>
                        <h3 className={cl.cardTitle}>Фото профиля</h3>
                        <div className={cl.avatarWrapper}>
                            <img
                                className={cl.avatar}
                                src={displayedPhoto}
                                alt="Фото профиля"
                                onError={(e) => { e.target.src = ava; }}
                            />
                            {imgFile && (
                                <button
                                    type="button"
                                    className={cl.avatarReset}
                                    onClick={() => setImgFile(null)}
                                    title="Отменить новый файл"
                                    aria-label="Отменить новый файл"
                                >
                                    <IconClose width={14} height={14}/>
                                </button>
                            )}
                        </div>

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            name="file"
                            onChange={handlePickFile}
                            className={cl.hiddenFileInput}
                        />
                        <button
                            type="button"
                            className={cl.uploadButton}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <IconUpload width={14} height={14}/>
                            <span>{imgFile ? 'Выбрать другое' : 'Загрузить фото'}</span>
                        </button>
                        {imgFile && (
                            <div className={cl.uploadHint}>
                                Будет загружено при сохранении: <strong>{imgFile.name}</strong>
                            </div>
                        )}
                        <div className={cl.uploadNote}>
                            Рекомендуем квадратное изображение,
                            формат JPG или PNG, до 2 МБ.
                        </div>
                    </aside>

                    {/* ===== Правая колонка: форма ===== */}
                    <section className={cl.formColumn}>

                        {/* Личные данные */}
                        <div className={cl.formCard}>
                            <div className={cl.formCardHead}>
                                <span className={cl.formCardIcon}>
                                    <IconUser width={18} height={18}/>
                                </span>
                                <h3 className={cl.formCardTitle}>Личные данные</h3>
                            </div>

                            <div className={cl.formGrid}>
                                <div className={cl.field}>
                                    <label className={cl.label}>Имя</label>
                                    <input
                                        type="text"
                                        value={firstName}
                                        onChange={(e) => setFirstName(e.target.value)}
                                        className={cl.input}
                                        placeholder="Например, Виктория"
                                    />
                                </div>
                                <div className={cl.field}>
                                    <label className={cl.label}>Фамилия</label>
                                    <input
                                        type="text"
                                        value={lastName}
                                        onChange={(e) => setlastName(e.target.value)}
                                        className={cl.input}
                                        placeholder="Например, Иванкова"
                                    />
                                </div>

                                <div className={cl.field}>
                                    <label className={cl.label}>Город</label>
                                    <input
                                        type="text"
                                        value={city}
                                        onChange={(e) => setCity(e.target.value)}
                                        className={cl.input}
                                        placeholder="Краснодар"
                                    />
                                </div>
                                <div className={cl.field}>
                                    <label className={cl.label}>Страна</label>
                                    <input
                                        type="text"
                                        value={country}
                                        onChange={(e) => setCountry(e.target.value)}
                                        className={cl.input}
                                        placeholder="Россия"
                                    />
                                </div>

                                <div className={`${cl.field} ${cl.fieldFull}`}>
                                    <label className={cl.label}>Статус</label>
                                    <input
                                        type="text"
                                        value={status}
                                        onChange={(e) => setStatus(e.target.value)}
                                        className={cl.input}
                                        placeholder="Например, открываю первый филиал"
                                    />
                                    <div className={cl.fieldHint}>
                                        Короткая фраза о себе. Отображается в профиле Майнкрафтии.
                                    </div>
                                </div>
                            </div>

                            <div className={cl.formActions}>
                                <button
                                    type="button"
                                    className={cl.saveButton}
                                    onClick={saveProfile}
                                    disabled={isSaving}
                                >
                                    <IconCheck width={14} height={14}/>
                                    <span>{isSaving ? 'Сохранение…' : 'Сохранить изменения'}</span>
                                </button>
                            </div>
                        </div>

                        {/* Опасная зона */}
                        <div className={cl.dangerCard}>
                            <div className={cl.dangerHead}>
                                <span className={cl.dangerIcon}>
                                    <IconAlert width={18} height={18}/>
                                </span>
                                <div>
                                    <h3 className={cl.dangerTitle}>Выход из аккаунта</h3>
                                    <p className={cl.dangerText}>
                                        Завершит сеанс в админ-панели. Вы сможете снова войти,
                                        используя тот же логин и пароль.
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                className={cl.logoutButton}
                                onClick={() => setShowExitConfirm(true)}
                            >
                                <IconLogout width={14} height={14}/>
                                <span>Выйти</span>
                            </button>
                        </div>

                    </section>
                </div>
            </div>

            {/* Подтверждение выхода */}
            {showExitConfirm && (
                <div className={cl.modalOverlay} onClick={() => setShowExitConfirm(false)}>
                    <div className={cl.modalCard} onClick={(e) => e.stopPropagation()}>
                        <header className={cl.modalHead}>
                            <div className={cl.modalTitleWrap}>
                                <span className={cl.modalIcon}>
                                    <IconAlert width={18} height={18}/>
                                </span>
                                <h2 className={cl.modalTitle}>Выйти из аккаунта?</h2>
                            </div>
                            <button
                                onClick={() => setShowExitConfirm(false)}
                                className={cl.modalClose}
                                aria-label="Закрыть"
                            >
                                <IconClose width={18} height={18}/>
                            </button>
                        </header>
                        <div className={cl.modalBody}>
                            <p className={cl.confirmText}>
                                Вы действительно хотите выйти из админ-панели?
                            </p>
                            <p className={cl.confirmHint}>
                                Несохранённые изменения будут потеряны.
                            </p>
                        </div>
                        <div className={cl.modalFooter}>
                            <button
                                onClick={() => setShowExitConfirm(false)}
                                className={cl.btnSecondary}
                            >
                                Отмена
                            </button>
                            <button
                                onClick={exitUser}
                                className={cl.btnDangerSolid}
                            >
                                <IconLogout width={14} height={14}/>
                                <span>Да, выйти</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SettingsAccount;
